import { ChangeDetectionStrategy, Component, OnInit } from '@angular/core';
import { forkJoin } from 'rxjs';
import { ClubLevel, ClubLevelInfo, LeaderboardEntry, PointsSummary, PointsTransaction, Reward } from 'src/app/models/community';
import { AuthService } from 'src/app/services/auth.service';
import { CommunityService } from 'src/app/services/community.service';
import { I18nService } from 'src/app/services/i18n.service';
import { formatDate } from '../account-sections';

interface EarnRule {
  reason: string;
  icon: string;
  points: number;
  hintParams: { [name: string]: number };
}

const LEVEL_ORDER: ClubLevel[] = ['bronze', 'silver', 'gold', 'platinum'];

@Component({
  selector: 'app-points-club',
  templateUrl: './points-club.component.html',
  styleUrls: ['./points-club.component.scss'],
  changeDetection: ChangeDetectionStrategy.Eager,
  standalone: false
})
export class PointsClubComponent implements OnInit {

  summary: PointsSummary | null = null;
  history: PointsTransaction[] = [];
  leaderboard: LeaderboardEntry[] = [];
  rewards: Reward[] = [];
  loading = true;
  error = '';
  showAllHistory = false;

  constructor(
    private communityService: CommunityService,
    private authService: AuthService,
    public i18n: I18nService
  ) { }

  ngOnInit(): void {
    this.load();
  }

  load(): void {
    this.loading = true;
    this.error = '';
    forkJoin({
      summary: this.communityService.loadPoints(),
      history: this.communityService.pointsHistory(100),
      leaderboard: this.communityService.leaderboard(),
      rewards: this.communityService.rewards(),
    }).subscribe({
      next: result => {
        Object.assign(this, result);
        this.loading = false;
      },
      error: err => {
        this.error = err || this.i18n.t('account.loadError');
        this.loading = false;
      }
    });
  }

  get myUserId(): string {
    return this.authService.currentUserValue?._id || '';
  }

  // Progression vers le niveau suivant (0-100)
  get levelProgress(): number {
    const s = this.summary;
    if (!s || !s.nextLevelAt) {
      return 100;
    }
    const current = s.levels.find(l => l.id === s.level)?.threshold || 0;
    return Math.max(0, Math.min(100, ((s.earned12m - current) / (s.nextLevelAt - current)) * 100));
  }

  get pointsToNextLevel(): number {
    return this.summary?.nextLevelAt ? Math.max(0, this.summary.nextLevelAt - this.summary.earned12m) : 0;
  }

  get earnRules(): EarnRule[] {
    const r = this.summary?.rules;
    const w = this.summary?.weekly;
    if (!r || !w) {
      return [];
    }
    return [
      { reason: 'dealApproved', icon: 'task_alt', points: r.pointsDealApproved, hintParams: { points: r.pointsDealApproved } },
      { reason: 'partnerBonus', icon: 'handshake', points: r.pointsPartnerBonus, hintParams: { points: r.pointsPartnerBonus } },
      { reason: 'firstDeal', icon: 'rocket_launch', points: r.pointsFirstDeal, hintParams: { points: r.pointsFirstDeal } },
      { reason: 'dealHot', icon: 'local_fire_department', points: r.pointsDealHot, hintParams: { points: r.pointsDealHot, threshold: r.hotThreshold } },
    ];
  }

  get weekSegments(): boolean[] {
    const w = this.summary?.weekly;
    if (!w) {
      return [];
    }
    return Array.from({ length: Math.max(w.required, 1) }, (_, i) => i < w.votes);
  }

  // Position (%) d'un palier de série sur la frise des bonus
  streakPosition(weeks: number): number {
    const bonuses = this.summary?.weekly.streakBonuses || [];
    const max = Math.max(...bonuses.map(b => b[0]), 1);
    return (weeks / max) * 100;
  }

  get streakProgress(): number {
    const bonuses = this.summary?.weekly.streakBonuses || [];
    const max = Math.max(...bonuses.map(b => b[0]), 1);
    return Math.min(100, ((this.summary?.weekly.streak || 0) / max) * 100);
  }

  get nextStreakBonus(): number[] | null {
    const streak = this.summary?.weekly.streak || 0;
    return (this.summary?.weekly.streakBonuses || []).find(b => b[0] > streak) || null;
  }

  isReached(level: ClubLevelInfo): boolean {
    return !!this.summary && LEVEL_ORDER.indexOf(this.summary.level) >= LEVEL_ORDER.indexOf(level.id);
  }

  rewardsForLevel(level: ClubLevel): Reward[] {
    return this.rewards.filter(r => r.minLevel === level);
  }

  get visibleHistory(): PointsTransaction[] {
    return this.showAllHistory ? this.history : this.history.slice(0, 10);
  }

  isLocked(transaction: PointsTransaction): boolean {
    return transaction.points > 0 && new Date(transaction.availableAt).getTime() > Date.now();
  }

  date(value: string, withYear = true): string {
    return formatDate(value, this.i18n.current, withYear);
  }
}
